import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize, map, Observable, tap } from 'rxjs';
import { blobToImage } from '@app/shared/utils/file.util';
import { maskPhoneNumber } from '@app/shared/utils/phone.util';
import { DocumentService } from '@app/documents/services/document.service';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { ProfileSandbox } from '@app/profile/sandboxes/profile.sandbox';
import { DOCUMENT_SIGN } from '@app/core/core.constants';
import { SignBatchDocument } from '../models/sign-document-batch.types';
import { SignDocumentBatchService } from '../services/sign-document-batch.service';

@Injectable({ providedIn: 'root' })
export class SignDocumentBatchSandbox {
  private readonly documentService = inject(DocumentService);
  private readonly signDocumentBatchService = inject(SignDocumentBatchService);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly profileSandbox = inject(ProfileSandbox);

  readonly documents = signal<SignBatchDocument[]>([]);
  readonly verificationCodeLength = signal<number | null>(null);
  readonly loadingBatch = signal(false);

  // The verification code is sent to the candidate's profile phone number; it is
  // read from profile state (no signing endpoint returns it) and masked for display.
  private readonly phoneNumber = toSignal(
    this.profileSandbox.user$.pipe(map((user) => user?.phoneNumber ?? '')),
    { initialValue: '' }
  );
  readonly maskedPhoneNumber = computed(() => maskPhoneNumber(this.phoneNumber()));

  private documentBatchId: string | null = null;

  private readonly previews = signal<Record<string, string>>({});
  private readonly previewLoading = signal<Record<string, boolean>>({});
  private readonly agreements = signal<Record<string, boolean>>({});

  readonly agreedCount = computed(
    () => this.documents().filter((document) => this.agreements()[document.documentId]).length
  );
  readonly remainingCount = computed(() => this.documents().length - this.agreedCount());
  readonly allAgreed = computed(() => this.documents().length > 0 && this.remainingCount() === 0);

  loadBatch(documentBatchId: string): void {
    this.documentBatchId = documentBatchId;
    // Ensure the profile (and thus the phone number shown on the verification
    // page) is populated by the time the user reaches the signing step.
    this.profileSandbox.loadUser();
    this.loadingBatch.set(true);
    this.signDocumentBatchService
      .getSignBatch(documentBatchId)
      .pipe(
        tap((batch) => {
          this.documents.set(batch.documents);
          this.verificationCodeLength.set(batch.verificationCodeLength);
        }),
        finalize(() => this.loadingBatch.set(false))
      )
      .subscribe();
  }

  downloadDocument(documentId: string): void {
    if (this.previews()[documentId] || this.previewLoading()[documentId]) {
      return;
    }
    this.setPreviewLoading(documentId, true);
    this.documentService
      .downloadDocument(documentId)
      .pipe(
        map(async (blob) => this.setPreview(documentId, await blobToImage(blob))),
        finalize(() => this.setPreviewLoading(documentId, false))
      )
      .subscribe();
  }

  startVerification(): void {
    this.recaptchaService.execute(DOCUMENT_SIGN, (token) =>
      this.signDocumentBatchService.startSigning(this.documentBatchId, token).subscribe()
    );
  }

  verify(verificationCode: string): Observable<void> {
    return this.signDocumentBatchService.verifySigning(this.documentBatchId, verificationCode);
  }

  previewFor(documentId: string): string | undefined {
    return this.previews()[documentId];
  }

  isPreviewLoading(documentId: string): boolean {
    return !!this.previewLoading()[documentId];
  }

  isAgreed(documentId: string): boolean {
    return !!this.agreements()[documentId];
  }

  setAgreed(documentId: string, value: boolean): void {
    this.agreements.update((state) => ({ ...state, [documentId]: value }));
  }

  cleanup(): void {
    this.documentBatchId = null;
    this.documents.set([]);
    this.verificationCodeLength.set(null);
    this.loadingBatch.set(false);
    this.previews.set({});
    this.previewLoading.set({});
    this.agreements.set({});
  }

  private setPreview(documentId: string, preview: string): void {
    this.previews.update((state) => ({ ...state, [documentId]: preview }));
  }

  private setPreviewLoading(documentId: string, loading: boolean): void {
    this.previewLoading.update((state) => ({ ...state, [documentId]: loading }));
  }
}
